import React, { useState, useEffect } from 'react';
import { RocketLaunchIcon, ClockIcon, UserGroupIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import PlaybookHero from '../../../components/playbooks/PlaybookHero';
import PlaybookMetrics from '../../../components/playbooks/PlaybookMetrics';
import VideoEmbed from '../../../components/playbooks/VideoEmbed';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';
import PlaybookSection from '../../../components/playbooks/PlaybookSection';
import { getResourceBySlug } from '../../../lib/db';

const TransparencyCharterKeyInfo = () => {
  const [resource, setResource] = useState(null);

  useEffect(() => {
    getResourceBySlug('transparency-charter').then(setResource);
  }, []);

  return (
    <div className="space-y-8">
      <PlaybookHero
        title={resource?.title || "Building A Transparency Charter"}
        subtitle={resource?.description || "A step-by-step guide to building a transparency charter within your startup"}
        category={resource?.category || "Culture"}
        bannerUrl={resource?.banner_url}
        readingTime={resource?.timeframe || "15 min read"}
        type={resource?.type}
      />

      <div className="border-l-4 border-primary-light pl-4 italic text-gray-300">
        <p className="text-lg">
          This Playbook is for you if any of the following (or similar) are true:
        </p>
        <ul className="mt-4 space-y-2">
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            Folks are complaining about lack of visibility on certain things (e.g. decisions made, finances, operational changes, development & pay)
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            'transparency' is a core value but still isn't defined, and it's being weaponised against you
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            Employees seem confused about what you share, what you don't share and why
          </li>
        </ul>
      </div>

      <PlaybookSection
        title="Guide Overview"
        icon={<RocketLaunchIcon className="h-7 w-7 text-primary-light" />}
        heading="Key Information"
      >
        <PlaybookMetrics
          metrics={[
            {
              icon: <ClockIcon className="h-6 w-6 text-primary-light" />,
              label: "Timeline",
              value: "2-4 Weeks"
            },
            {
              icon: <UserGroupIcon className="h-6 w-6 text-primary-light" />,
              label: "Team Size",
              value: "Any"
            },
            {
              icon: <QuestionMarkCircleIcon className="h-6 w-6 text-primary-light" />,
              label: "Why Use",
              value: "Align your team around a common understanding & definition of what 'transparency' means in your company"
            },
            {
              icon: <QuestionMarkCircleIcon className="h-6 w-6 text-primary-light" />,
              label: "When to Use",
              value: "If you're generally seen as a transparent business but still haven't defined it formally yet, or if you are starting to experience problems with clarity & understanding internally"
            }
          ]}
        />
      </PlaybookSection>

      <PlaybookSection
        title="Watch This"
        icon={<RocketLaunchIcon className="h-7 w-7 text-primary-light" />}
        heading="Video Overview"
      >
        <VideoEmbed
          src="https://www.loom.com/embed/f2b1489c0fd4401080ae2bc4c72bd4c3"
          title="Transparency Charter Overview"
        />
      </PlaybookSection>

      <PlaybookSection
        title="What Is This"
        icon={<RocketLaunchIcon className="h-7 w-7 text-primary-light" />}
        heading="Purpose"
      >
        <div className="prose prose-invert max-w-none">
          <p className="text-gray-300">
            Welcome to your step-by-step guide on building a transparency charter within your startup. This playbook is designed to help startup people leaders plan, design, document and embed a common mindset and understanding of what it means to be transparent in your business.
          </p>
        </div>
      </PlaybookSection>

      <PlaybookSection
        title="What You'll Need"
        icon={<RocketLaunchIcon className="h-7 w-7 text-primary-light" />}
        heading="Prerequisites"
      >
        <ul className="space-y-4">
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            Time! A few hours overall to prep, facilitate workshops, and document along the way
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            Leadership Team's time (1-2 hours for a workshop)
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            Possible employee time (if you opt for co-creation opportunities)
          </li>
        </ul>
      </PlaybookSection>

      <PlaybookSection
        title="Obstacles & How To Overcome Them"
        icon={<RocketLaunchIcon className="h-7 w-7 text-primary-light" />}
        heading="Potential Challenges"
      >
        <div className="space-y-6">
          <ExpandableCallout
            type="warning"
            title="Disagreement & Conflict on Transparency Levels"
            defaultOpen={true}
          >
            <div className="space-y-4">
              <p className="text-gray-300">
                Whilst co-creating this thing is important, it may still be easier to identify and agree one person who can be ultimately accountable for making a final decision on something in the event of any disagreement here; most likely the Founder/CEO in the majority of cases.
              </p>
              <p className="text-gray-300">
                Alternatively, it may be useful to put a pause on that particular area or topic (e.g. finances), come back to it later on, or take it to the wider team to gauge opinion on wider group sentiment and desire for openness.
              </p>
            </div>
          </ExpandableCallout>

          <ExpandableCallout
            type="warning"
            title="Push back from employees on areas of the Transparency Charter"
            defaultOpen={true}
          >
            <div className="space-y-4">
              <p className="text-gray-300">
                It's important to stress the 'why' behind the decisions you have made. Assuming trust is at the right level within the organisation employees will hopefully largely understand the decisions you have made, even if they don't necessarily agree with them. It's ok for people to not agree with everything.
              </p>
              <p className="text-gray-300">
                It could also be useful to stress that this is a living, breathing charter. One that you plan on iterating on and reviewing. Committing to that and being open to changing what you have landed on should it not be conducive to building the culture you want will help to settle any nerves there.
              </p>
            </div>
          </ExpandableCallout>
        </div>
      </PlaybookSection>
    </div>
  );
};

export default TransparencyCharterKeyInfo; 